import { z } from 'zod';
import { RUN_MODES, SOURCE_TYPES, DESTINATION_TYPES } from './agents.js';
// Action API DTOs (snake_case wire contract). An action is
// a user-saved instance of a SINGLE catalog action — picked from the catalog and
// runnable once or on a schedule. See packages/db/src/schema/actions.ts
// and the Actions IA plan. (Replaces the deprecated multi-agent `automations`.)
/** Scope of an action: org-wide or a set of projects. */
export const ACTION_SCOPES = ['org', 'project'];
/** Lifecycle: added-but-unconfigured vs ready to run. */
export const ACTION_STATUSES = ['draft', 'ready'];
// ── Sources & Destinations (symmetric I/O) ────────────────────────────────
// An action reads from Sources[] and writes to Destinations[]. A `project` is
// the first source type; integrations (CRM, SEO) plug in later via `connections`.
// Destinations subsume the old "notify task" + "Then" step: `notify` is one type
// alongside `webhook`, `crm` (write-back), `action` (chaining), `internal`
// (e.g. ICP→profile). Stored in `actions.options.{sources,destinations}`
// for now (no migration); may promote to columns later.
export const actionSourceSchema = z.object({
    type: z.enum(SOURCE_TYPES),
    /** projectId, or a `connections` row id for integrations. */
    id: z.string().max(128),
});
export const actionDestinationSchema = z.object({
    type: z.enum(DESTINATION_TYPES),
    /**
     * Per-type config: notify → { channel, recipient/webhook, subject?, message };
     * webhook → { url, headers?, body? }; crm → { integrationId, object, mapping };
     * action → { actionId }; internal → {}.
     */
    config: z.record(z.string(), z.unknown()).default({}),
});
// Signal trigger (GTM Signals & Target Accounts, PRD 3.1). A signal-triggered
// action fires when a matching signal is recorded. `scope` narrows
// which accounts count: 'targets' = only Target Accounts, 'all' = any account.
// Types-only here — matching/dispatch happens in the SignalService bridge; the
// existing cron/event variants are untouched and this field is optional (fully
// backward compatible).
export const SIGNAL_TRIGGER_SCOPES = ['targets', 'all'];
export const signalTriggerSchema = z.object({
    type: z.string().max(64),
    scope: z.enum(SIGNAL_TRIGGER_SCOPES).optional(),
});
// Run config. `mode='schedule'` requires `cron`; `mode='trigger'` requires
// `event` (or, for GTM signal triggers, `signal`). Scheduled EXECUTION lands
// later — this stores config only.
export const actionScheduleSchema = z.object({
    cron: z.string().max(120).optional(),
    event: z.string().max(160).optional(),
    // GTM Signals: fire when a matching signal is recorded. Optional & additive.
    signal: signalTriggerSchema.optional(),
});
// One-hop chain destination (GTM action chaining, PRD 3.2). The reserved
// `'action'` destination type runs a referenced agent action FIRST — deriving
// its inputs from the triggering signal's company/account — then delivers that
// run's result through the triggering action's remaining notify/webhook
// destinations. Types-only here: the executor and the one-hop guard (an
// `'action'` destination is rejected on any action that is itself a chain
// target) live in the chain service. This does NOT change the base
// `actionDestinationSchema` above — it is a stricter, opt-in shape for the
// `'action'` variant's `config`.
export const actionChainDestinationConfigSchema = z.object({
    action_key: z.string().max(64),
    params_from: z.literal('signal'),
});
export const actionChainDestinationSchema = z.object({
    type: z.literal('action'),
    config: actionChainDestinationConfigSchema,
});
export const actionSchema = z.object({
    id: z.string(),
    name: z.string(),
    action_key: z.string(),
    status: z.enum(ACTION_STATUSES),
    scope: z.enum(ACTION_SCOPES),
    project_ids: z.array(z.string()),
    profile_id: z.string().nullable(),
    agent_overrides: z.record(z.string(), z.boolean()),
    options: z.record(z.string(), z.unknown()),
    memory_override: z.string().nullable(),
    run_mode: z.enum(RUN_MODES),
    schedule: actionScheduleSchema.nullable(),
    advanced: z.record(z.string(), z.unknown()),
    enabled: z.boolean(),
    // Status-table columns: next scheduled fire (schedule mode only) + the most
    // recent run, resolved from action_runs by the list endpoint.
    next_run_at: z.string().nullable(),
    last_run_at: z.string().nullable(),
    last_run_status: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});
export const actionsListResponseSchema = z.object({
    actions: z.array(actionSchema),
});
// Create: `name` + `action_key` required. `status` defaults to 'draft' (the
// "Add to My Actions" path); the "Add & Configure" path sends 'ready' with the
// rest filled in. Cross-field rules (action_key ∈ catalog, run_mode ∈ the
// action's runModes, scope/project_ids coherence, profile ownership, schedule
// requires cron/event) are validated in the service against the live catalog.
export const createActionRequestSchema = z.object({
    name: z.string().min(1).max(160),
    action_key: z.string().min(1).max(64),
    status: z.enum(ACTION_STATUSES).default('draft'),
    scope: z.enum(ACTION_SCOPES).default('org'),
    project_ids: z.array(z.string().max(64)).max(200).default([]),
    profile_id: z.string().max(64).nullable().optional(),
    agent_overrides: z.record(z.string(), z.boolean()).optional(),
    options: z.record(z.string(), z.unknown()).optional(),
    memory_override: z.string().max(8000).nullable().optional(),
    run_mode: z.enum(RUN_MODES).default('once'),
    schedule: actionScheduleSchema.nullable().optional(),
    advanced: z.record(z.string(), z.unknown()).optional(),
    enabled: z.boolean().optional(),
});
// Patch: every field optional; only provided keys are written.
export const patchActionRequestSchema = createActionRequestSchema.partial();
// Run once now. `project_id` picks which project a project-scoped action targets
// when it lists more than one (optional; defaults to the first).
export const runActionRequestSchema = z.object({
    project_id: z.string().max(64).optional(),
});
//# sourceMappingURL=actions.js.map